"use client";

import React from 'react';
import { StarRating } from '@/components/StarRating';
import { useRouter } from 'next/navigation';
import { getApiBaseUrl } from '@/lib/api';
import { clearToken, getToken } from '@/lib/auth';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';

type Review = {
  id: string;
  authorName: string;
  rating: number;
  text: string;
  createdAt: string;
  avatarUrl?: string | null;
  likeCount?: number;
  dislikeCount?: number;
  score?: number;
  userVote?: number | null;
};

function StarRatingDisplay({ rating }: { rating: number }) {
  return <StarRating rating={rating} size="md" />;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export function ReviewList({
  reviews,
  casinoSlug,
}: {
  reviews: Review[];
  casinoSlug?: string;
}) {
  const router = useRouter();
  const locale = useLocale();
  const [token, setToken] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Review[]>(reviews);
  const [pendingVoteId, setPendingVoteId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setItems(reviews);
  }, [reviews]);

  React.useEffect(() => {
    setToken(getToken());
    const handler = () => setToken(getToken());
    window.addEventListener('auth:token', handler);
    return () => window.removeEventListener('auth:token', handler);
  }, []);

  React.useEffect(() => {
    const fetchUserVotes = async () => {
      if (!token || !casinoSlug) {
        return;
      }
      const response = await fetch(
        `${getApiBaseUrl()}/reviews?status=APPROVED&page=1&pageSize=200&casinoSlug=${encodeURIComponent(
          casinoSlug,
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        },
      );
      if (response.status === 401 || response.status === 403) {
        clearToken();
        setToken(null);
        return;
      }
      if (!response.ok) {
        return;
      }
      const data = (await response.json()) as {
        items: Review[];
      };
      setItems((current) =>
        current.map((review) => {
          const updated = data.items.find((item) => item.id === review.id);
          if (!updated) {
            return review;
          }
          return {
            ...review,
            likeCount: updated.likeCount ?? review.likeCount,
            dislikeCount: updated.dislikeCount ?? review.dislikeCount,
            score: updated.score ?? review.score,
            userVote: updated.userVote ?? null,
          };
        }),
      );
    };

    fetchUserVotes();
  }, [token, casinoSlug]);

  const handleVote = async (reviewId: string, value: 1 | -1) => {
    const currentToken = getToken();
    if (!currentToken) {
      router.push(getPathForLocale(locale, '/register'));
      return;
    }

    const currentReview = items.find((review) => review.id === reviewId);
    if (!currentReview) {
      return;
    }

    const currentVote = currentReview.userVote ?? null;
    const nextVote = currentVote === value ? null : value;
    const likeCount = currentReview.likeCount ?? 0;
    const dislikeCount = currentReview.dislikeCount ?? 0;
    const likeDelta =
      (currentVote === 1 ? -1 : 0) + (nextVote === 1 ? 1 : 0);
    const dislikeDelta =
      (currentVote === -1 ? -1 : 0) + (nextVote === -1 ? 1 : 0);
    const optimistic = {
      ...currentReview,
      likeCount: Math.max(0, likeCount + likeDelta),
      dislikeCount: Math.max(0, dislikeCount + dislikeDelta),
      score: (likeCount + likeDelta) - (dislikeCount + dislikeDelta),
      userVote: nextVote,
    };

    setItems((current) =>
      current.map((review) => (review.id === reviewId ? optimistic : review)),
    );
    setPendingVoteId(reviewId);

    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}/vote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ value }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          clearToken();
          router.push(getPathForLocale(locale, '/register'));
        }
        setItems((current) =>
          current.map((review) =>
            review.id === reviewId ? currentReview : review,
          ),
        );
        return;
      }

      const data = (await response.json()) as {
        likeCount: number;
        dislikeCount: number;
        score: number;
        userVote: number | null;
      };

      setItems((current) =>
        current.map((review) =>
          review.id === reviewId
            ? {
                ...review,
                likeCount: data.likeCount,
                dislikeCount: data.dislikeCount,
                score: data.score,
                userVote: data.userVote,
              }
            : review,
        ),
      );
    } finally {
      setPendingVoteId((current) => (current === reviewId ? null : current));
    }
  };
  if (!reviews.length) {
    return (
      <p className="text-sm text-slate-400">
        No reviews yet. Be the first to share feedback.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((review) => {
        const likeCount = review.likeCount ?? 0;
        const dislikeCount = review.dislikeCount ?? 0;
        const score = review.score ?? likeCount - dislikeCount;

        return (
        <div key={review.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5">
              {review.avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={review.avatarUrl}
                  alt={review.authorName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-white">
                  {getInitials(review.authorName)}
                </span>
              )}
            </div>
            {/* Review content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">
                    {review.authorName}
                  </p>
                  <p className="text-xs text-slate-400">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <StarRatingDisplay rating={review.rating} />
              </div>
              <p className="mt-2 text-sm text-slate-200">{review.text}</p>
              <div className="mt-3 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={`flex items-center gap-1 rounded-full border px-2 py-1 transition ${
                      review.userVote === 1
                        ? 'border-[#ffb74d] text-[#ffb74d]'
                        : 'border-white/10 text-slate-300 hover:border-white/20 hover:text-white'
                    }`}
                    onClick={() => handleVote(review.id, 1)}
                    aria-label="Like"
                    disabled={pendingVoteId === review.id}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M10.5 3c-.276 0-.5.224-.5.5v3.09l-3.64 4.37a1.5 1.5 0 0 0-.36.96V16a1.5 1.5 0 0 0 1.5 1.5h5.01a2 2 0 0 0 1.92-1.44l1.22-4.27a2 2 0 0 0-1.92-2.56H12V3.5c0-.276-.224-.5-.5-.5h-1z" />
                    </svg>
                  </button>
                  <span
                    className={`rounded-full border px-2 py-1 ${
                      score < 0
                        ? 'border-rose-400/40 text-rose-300'
                        : 'border-white/10 text-slate-300'
                    }`}
                  >
                    {score}
                  </span>
                  <button
                    type="button"
                    className={`flex items-center gap-1 rounded-full border px-2 py-1 transition ${
                      review.userVote === -1
                        ? 'border-rose-400 text-rose-400'
                        : 'border-white/10 text-slate-300 hover:border-white/20 hover:text-white'
                    }`}
                    onClick={() => handleVote(review.id, -1)}
                    aria-label="Dislike"
                    disabled={pendingVoteId === review.id}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M9.5 17c.276 0 .5-.224.5-.5v-3.09l3.64-4.37a1.5 1.5 0 0 0 .36-.96V4a1.5 1.5 0 0 0-1.5-1.5H7.49a2 2 0 0 0-1.92 1.44L4.35 8.21a2 2 0 0 0 1.92 2.56H8V16.5c0 .276.224.5.5.5h1z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
